import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '../AppIcon';
import { useLazyPlacesAutocompleteQuery, useLazyPlaceDetailsQuery } from '../../features/api/apiSlice';

/**
 * Typeahead place search. On select, calls onPlaceSelected with:
 * { address, city, state, country, latitude, longitude, formatted_address, place_id }
 */
const PlacesAutocomplete = ({
  label = 'Search place',
  value = '',
  onChange,
  onPlaceSelected,
  placeholder = 'Start typing an address or area…',
  required = false,
  helperText,
  className = '',
  countryBias = 'ug',
}) => {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  const [searchPlaces, { data: predictions = [], isFetching }] =
    useLazyPlacesAutocompleteQuery();
  const [fetchDetails, { isFetching: loadingDetails }] = useLazyPlaceDetailsQuery();

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const runSearch = useCallback(
    (text) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (text.trim().length >= 2) {
          searchPlaces({ input: text.trim(), country: countryBias });
          setOpen(true);
          setHighlight(0);
        }
      }, 280);
    },
    [searchPlaces, countryBias]
  );

  const handleInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    onChange?.(v);
    runSearch(v);
  };

  const selectPrediction = async (pred) => {
    setOpen(false);
    setQuery(pred.description || pred.main_text || '');
    try {
      const res = await fetchDetails({ place_id: pred.place_id }).unwrap();
      const place = res?.data || res;
      if (place) {
        const filled = {
          address: place.address || place.formatted_address || pred.description || '',
          city: place.city || '',
          state: place.state || '',
          country: place.country || '',
          latitude: place.latitude || '',
          longitude: place.longitude || '',
          formatted_address: place.formatted_address || pred.description || '',
          place_id: place.place_id || pred.place_id,
        };
        setQuery(filled.formatted_address || filled.address);
        onPlaceSelected?.(filled);
      }
    } catch {
      // Still pass description as address if details fail
      onPlaceSelected?.({
        address: pred.description || '',
        city: '',
        state: '',
        country: '',
        latitude: '',
        longitude: '',
        formatted_address: pred.description || '',
        place_id: pred.place_id,
      });
    }
  };

  const list = Array.isArray(predictions) ? predictions : predictions?.predictions || [];

  return (
    <div className={`relative ${className}`} ref={wrapRef}>
      {label && (
        <label className="block text-sm font-medium text-slate-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <div className="relative">
        <Icon
          name="MapPin"
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
        />
        <input
          type="text"
          value={query}
          onChange={handleInput}
          onFocus={() => list.length > 0 && setOpen(true)}
          onKeyDown={(e) => {
            if (!open || list.length === 0) return;
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setHighlight((h) => Math.min(h + 1, list.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlight((h) => Math.max(h - 1, 0));
            } else if (e.key === 'Enter' && list[highlight]) {
              e.preventDefault();
              selectPrediction(list[highlight]);
            } else if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
          placeholder={placeholder}
          autoComplete="off"
          className="w-full h-10 rounded-md border border-slate-200 bg-white pl-9 pr-9 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
        />
        {(isFetching || loadingDetails) && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
            …
          </span>
        )}
      </div>
      {helperText && <p className="mt-1 text-xs text-slate-500">{helperText}</p>}

      {open && list.length > 0 && (
        <ul className="absolute z-40 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg py-1">
          {list.map((p, i) => (
            <li key={p.place_id || i}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectPrediction(p)}
                className={`w-full text-left px-3 py-2.5 text-sm flex gap-2 items-start ${
                  i === highlight ? 'bg-slate-100' : 'hover:bg-slate-50'
                }`}
              >
                <Icon name="MapPin" size={14} className="mt-0.5 text-slate-400 shrink-0" />
                <span>
                  <span className="font-medium text-slate-900 block">
                    {p.main_text || p.description}
                  </span>
                  {p.secondary_text && (
                    <span className="text-xs text-slate-500">{p.secondary_text}</span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PlacesAutocomplete;
